"use client";

import { useState, type FormEvent } from "react";
import type { ColorRule } from "@/prisma/generated/client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

import { addColorRule, editColorRule } from "@/app/lib/actions/color-rules";

type FormValues = {
  brandLetter: ColorRule["brandLetter"];
  year: string;
  exclusiveProgram: ColorRule["exclusiveProgram"];
  codeColor: string;
  labelColor: string;
  dominantNuanceCode: number;
  dominantColorCode: number;
  versionNote?: string;
};

const currentYear = String(new Date().getFullYear());

const DEFAULT_FORM: FormValues = {
  brandLetter: "A",
  year: currentYear,
  exclusiveProgram: "N",
  codeColor: "",
  labelColor: "",
  dominantNuanceCode: 0,
  dominantColorCode: 0,
  versionNote: "",
};

export default function ColorRuleForm({
  rule,
  open,
  onClose,
  onSuccess,
}: {
  rule: ColorRule | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const isNew = !rule;
  const [form, setForm] = useState<FormValues>(() =>
    rule
      ? {
          brandLetter: rule.brandLetter,
          year: rule.year,
          exclusiveProgram: rule.exclusiveProgram,
          codeColor: rule.codeColor,
          labelColor: rule.labelColor,
          dominantNuanceCode: rule.dominantNuanceCode,
          dominantColorCode: rule.dominantColorCode,
          versionNote: "",
        }
      : DEFAULT_FORM,
  );

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const set = (key: keyof FormValues) => (value: unknown) =>
    setForm((f) => ({ ...f, [key]: value }) as FormValues);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const payload = {
      ...(isNew ? {} : { id: rule!.id }),
      ...form,
      codeColor: form.codeColor.toUpperCase(),
      labelColor: form.labelColor,
      versionNote: form.versionNote || undefined,
      isActive: true,
    };

    const result = isNew
      ? await addColorRule(payload)
      : await editColorRule(payload);

    setLoading(false);

    if (!result.success) {
      setError(result.message ?? "Erreur de validation");
      return;
    }

    onSuccess();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? "Nouvelle règle Couleur / Nuance"
              : `Modifier — ${rule?.brandLetter} · ${rule?.year} · ${rule?.exclusiveProgram} · (${rule?.dominantNuanceCode}/${rule?.dominantColorCode})`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {error && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Code marque</Label>
              <Select
                value={form.brandLetter}
                onValueChange={(v) => set("brandLetter")(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["V", "A", "N", "S", "C"] as const).map((b) => (
                    <SelectItem key={b} value={b}>
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Label>Année</Label>
              <Input
                value={form.year}
                onChange={(e) => set("year")(e.target.value)}
                placeholder="Ex : 2026"
                required
                disabled={loading}
                inputMode="numeric"
                maxLength={4}
                className="font-mono tracking-widest uppercase"
              />
            </div>

            <div className="space-y-2">
              <Label>Programme couleur exclusive</Label>
              <Select
                value={form.exclusiveProgram}
                onValueChange={(v) => set("exclusiveProgram")(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="O">O</SelectItem>
                  <SelectItem value="N">N</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Code couleur (4 caractères)</Label>
            <Input
              value={form.codeColor}
              onChange={(e) => set("codeColor")(e.target.value)}
              placeholder="Ex : A1B2"
              required
              disabled={loading}
              className="font-mono tracking-widest uppercase"
            />
          </div>

          <div className="space-y-2">
            <Label>Libellé couleur</Label>
            <Input
              value={form.labelColor}
              onChange={(e) => set("labelColor")(e.target.value)}
              placeholder="Ex : Bleu nuit"
              required
              disabled={loading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Dominante n°1 (nuance)</Label>
              <Select
                value={String(form.dominantNuanceCode)}
                onValueChange={(v) => set("dominantNuanceCode")(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 - Clair</SelectItem>
                  <SelectItem value="1">1 - Foncé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Dominante n°2 (couleur)</Label>
              <Select
                value={String(form.dominantColorCode)}
                onValueChange={(v) => set("dominantColorCode")(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 - Noir</SelectItem>
                  <SelectItem value="1">1 - Marron</SelectItem>
                  <SelectItem value="2">2 - Rouge</SelectItem>
                  <SelectItem value="3">3 - Orange</SelectItem>
                  <SelectItem value="4">4 - Jaune</SelectItem>
                  <SelectItem value="5">5 - Vert</SelectItem>
                  <SelectItem value="6">6 - Bleu</SelectItem>
                  <SelectItem value="7">7 - Beige</SelectItem>
                  <SelectItem value="8">8 - Gris</SelectItem>
                  <SelectItem value="9">9 - Blanc</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {!isNew && (
            <div className="space-y-2">
              <Label>
                Note de modification{" "}
                <span className="text-muted-foreground font-normal">
                  (optionnel)
                </span>
              </Label>
              <Input
                value={form.versionNote ?? ""}
                onChange={(e) => set("versionNote")(e.target.value)}
                placeholder="Motif de la modification…"
                disabled={loading}
              />
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Enregistrement…" : isNew ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
